/**
 * DeactivateProfessionalDialog Component
 * Confirmation dialog for deactivating professional account
 */

import { useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@shared/components/ui/dialog'
import { Button } from '@shared/components/ui/button'
import { AlertTriangle, Loader2 } from 'lucide-react'
import { logger } from '@shared/lib/utils/logger'

// ============================================================================
// Types
// ============================================================================

interface DeactivateProfessionalDialogProps {
  onConfirm: () => Promise<void>
  disabled?: boolean
}

// ============================================================================
// Component
// ============================================================================

export function DeactivateProfessionalDialog({
  onConfirm,
  disabled,
}: DeactivateProfessionalDialogProps) {
  const [isDeactivating, setIsDeactivating] = useState(false)
  const [open, setOpen] = useState(false)

  const handleConfirm = async () => {
    setIsDeactivating(true)
    try {
      await onConfirm()
      setOpen(false)
    } catch (error) {
      logger.error('Failed to deactivate:', error)
    } finally {
      setIsDeactivating(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          type="button"
          variant="destructive"
          disabled={disabled}
          className="w-full"
        >
          <AlertTriangle className="h-4 w-4 mr-2" />
          Desactivar Cuenta Profesional
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>¿Desactivar cuenta profesional?</DialogTitle>
          <DialogDescription>
            Tu perfil profesional dejará de ser visible públicamente. Los usuarios no podrán
            encontrarte en búsquedas ni contactarte como profesional.
            <br />
            <br />
            Podrás reactivar tu cuenta en cualquier momento desde esta pantalla.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="flex gap-2 sm:justify-end">
          <Button
            type="button"
            variant="outline"
            onClick={() => setOpen(false)}
            disabled={isDeactivating}
          >
            Cancelar
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={() => void handleConfirm()}
            disabled={isDeactivating}
          >
            {isDeactivating ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Desactivando...
              </>
            ) : (
              'Desactivar'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
